import { defaultGridSettings, SETTINGS_KEY } from '@/app/atoms/gridSettingsAtom';
import { atom } from 'jotai';
import type { Chat } from 'quadratic-shared/typesAndSchemasAI';
import type {
  ContextUsageState,
  EmptyChatSuggestionsState,
  FailingSqlConnectionsState,
  ImportFilesToGridState,
  PdfImportState,
  PromptSuggestionsState,
  QueuedPrompt,
  WebSearchState,
} from './types';
import { createDefaultChat } from './types';

// Read the user's "show on startup" preference from localStorage at module
// load so the panel's initial render reflects the saved setting. Without
// this, the atom defaults to false and the panel pops in once
// `initializeAIAnalyst` runs in a post-render effect, causing the grid to
// shift and visibly flicker on every cold file open.
const getInitialShowAIAnalyst = (): boolean => {
  try {
    if (typeof window === 'undefined') return defaultGridSettings.showAIAnalystOnStartup;
    const saved = window.localStorage.getItem(SETTINGS_KEY);
    if (!saved) return defaultGridSettings.showAIAnalystOnStartup;
    const parsed = JSON.parse(saved);
    return typeof parsed?.showAIAnalystOnStartup === 'boolean'
      ? parsed.showAIAnalystOnStartup
      : defaultGridSettings.showAIAnalystOnStartup;
  } catch {
    return defaultGridSettings.showAIAnalystOnStartup;
  }
};

// ============================================================================
// Initialization State
// ============================================================================

/** Whether the AI Analyst has been initialized */
export const aiAnalystInitializedAtom = atom(false);

// ============================================================================
// UI State Atoms
// ============================================================================

/** Whether the AI Analyst panel is visible */
export const showAIAnalystAtom = atom(getInitialShowAIAnalyst());

/** Whether the chat history view is shown */
export const showChatHistoryAtom = atom(false);

/** Active schema connection UUID for SQL context */
export const activeSchemaConnectionUuidAtom = atom<string | undefined>(undefined);

// ============================================================================
// Loading State Atoms
// ============================================================================

/** Whether an AI request is in progress */
export const loadingAtom = atom(false);

/** Abort controller for the current AI request */
export const abortControllerAtom = atom<AbortController | undefined>(undefined);

/** Message index we're waiting on (for billing limit handling) */
export const waitingOnMessageIndexAtom = atom<number | undefined>(undefined);

/** Queue of prompts waiting to be executed after the current one finishes */
export const promptQueueAtom = atom<QueuedPrompt[]>([]);

/** Whether queue processing is paused by the user */
export const promptQueuePausedAtom = atom(false);

// ============================================================================
// Chat State Atoms
// ============================================================================

/** All saved chats */
export const chatsAtom = atom<Chat[]>([]);

/** Current active chat (internal base atom) */
export const currentChatBaseAtom = atom<Chat>(createDefaultChat());

// ============================================================================
// Feature State Atoms
// ============================================================================

/** Prompt suggestions state */
export const promptSuggestionsAtom = atom<PromptSuggestionsState>({
  abortController: undefined,
  suggestions: [],
});

/** PDF import operation state */
export const pdfImportAtom = atom<PdfImportState>({
  abortController: undefined,
  loading: false,
});

/** Web search operation state */
export const webSearchAtom = atom<WebSearchState>({
  abortController: undefined,
  loading: false,
});

/** Import files to grid operation state */
export const importFilesToGridAtom = atom<ImportFilesToGridState>({
  loading: false,
});

/** Failing SQL connections tracking */
export const failingSqlConnectionsAtom = atom<FailingSqlConnectionsState>({
  uuids: [],
  lastResetTimestamp: 0,
});

/** Context usage from AI API responses */
export const contextUsageAtom = atom<ContextUsageState>({
  usage: undefined,
});

/** Empty chat suggestions state (categorized prompts shown when chat is empty) */
export const emptyChatSuggestionsAtom = atom<EmptyChatSuggestionsState>({
  suggestions: undefined,
  contextHash: undefined,
  loading: false,
  abortController: undefined,
});
